/**
 * The ways forward under a readiness result: the results video with the trial
 * under it, the NOVA offer for coaches under $100K, and the strategy call for
 * coaches past it. The public results page and the in-app score page both use
 * them; each passes the call to action that fits the viewer, and the in-app
 * page passes the admin's Book-a-call link when one is set.
 *
 * Aliases: nova offer block, strategy call section, readiness cta, results
 * video, vsl section
 */

import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";

import { LibraryVideoPlayer } from "~/components/learning/library-video-player";
import { ButtonLink } from "~/components/ui/button";
import { cn } from "~/lib/cn";
import { publicPlanPricingOptions } from "~/lib/functions/plan-pricing";
import { publicPlacementVideoQueryOptions } from "~/lib/queries/learningQuery";
import { formatNovaPrice } from "~/lib/shared/nova/format-price";
import { STRATEGY_CALL_URL } from "~/lib/shared/readiness/routes";

/**
 * "7 days free, then $100.00/month. Cancel any time." with the live Stripe
 * price, so the page never states a price the checkout does not charge.
 */
export function useNovaTrialFinePrint(): string {
  const { data: plans } = useQuery(publicPlanPricingOptions);
  const monthly = plans?.find((plan) => plan.planKey === "nova")?.monthly;
  return monthly
    ? `7 days free, then ${formatNovaPrice(monthly)}. Cancel any time.`
    : "7 days free. Cancel any time.";
}

/**
 * Where "Book a strategy call" goes: the admin's Book-a-call link, which
 * leaves the app in a new tab, or else the email to the team.
 */
function strategyCallLink(bookACallUrl: string | null | undefined): {
  href: string;
  target?: "_blank";
  rel?: string;
} {
  return bookACallUrl
    ? { href: bookACallUrl, target: "_blank", rel: "noopener noreferrer" }
    : { href: STRATEGY_CALL_URL };
}

/**
 * `marketing` is the content of the public results page's full-width purple
 * band; the page draws the band. `app` is the signed-in score page's panel,
 * which follows the app page scale: a section title and a plain panel.
 */
export type ReadinessOfferVariant = "marketing" | "app";

const OFFER_STYLES: Record<
  ReadinessOfferVariant,
  { panel: string; title: string; buy: string; action: string }
> = {
  marketing: {
    panel: "@container",
    title:
      "font-heading text-3xl font-medium tracking-tight text-balance text-gray-900 sm:text-4xl",
    buy: "rounded-3xl border border-gray-200 bg-white p-7 text-center shadow-xl shadow-purple-950/5",
    action: "flex justify-center *:w-full",
  },
  app: {
    panel: "@container rounded-2xl bg-purple-50 p-6",
    title: "font-heading text-xl font-medium text-balance text-gray-900",
    buy: "rounded-2xl bg-white p-6 text-center shadow-[0_10px_30px_-12px_rgba(60,20,90,0.22)]",
    action: "flex justify-center",
  },
};

/**
 * The video an admin sets at the "6-Figure Readiness Score results"
 * placement. It is a public placement, so both score pages read the one
 * video, whoever is signed in.
 */
export const READINESS_RESULTS_VIDEO_QUERY =
  publicPlacementVideoQueryOptions("readiness_results");

const RESULTS_VIDEO_STYLES: Record<
  ReadinessOfferVariant,
  { section: string; title: string; player: string; action: string }
> = {
  marketing: {
    section: "pt-14 md:pt-20",
    title:
      "mx-auto max-w-3xl font-heading text-3xl font-medium tracking-tight text-balance text-gray-900 sm:text-4xl",
    player: "mx-auto mt-8 max-w-4xl overflow-hidden rounded-3xl",
    action: "mt-8",
  },
  app: {
    section: "",
    title:
      "mx-auto max-w-2xl font-heading text-xl font-medium text-balance text-gray-900",
    player: "mx-auto mt-6 max-w-3xl overflow-hidden rounded-2xl",
    action: "mt-6",
  },
};

/**
 * The results video with the trial under it. Without a video the section is
 * gone. The caller decides who sees it; past $100K the strategy call leads
 * instead.
 */
export function ReadinessResultsVideo({
  action,
  fine,
  variant = "marketing",
  className,
}: {
  action: React.ReactNode;
  fine: string | null;
  variant?: ReadinessOfferVariant;
  className?: string;
}): React.JSX.Element | null {
  const { data: video } = useQuery(READINESS_RESULTS_VIDEO_QUERY);
  if (!video) return null;
  const styles = RESULTS_VIDEO_STYLES[variant];
  return (
    <section className={cn("text-center", styles.section, className)}>
      <h2 className={styles.title}>
        Watch this video to see what your score means and how you can get to
        $100K per year
      </h2>
      <LibraryVideoPlayer video={video} className={styles.player} />
      <div className={cn("flex justify-center", styles.action)}>{action}</div>
      {fine ? (
        <p className="text-text-tertiary mt-3 text-sm text-pretty">{fine}</p>
      ) : null}
    </section>
  );
}

const NOVA_BUILDS = [
  "NOVA AI, your business builder",
  "The 8-week Business Builder Program",
  "Your positioning, offer and content, written with you",
] as const;

export function ReadinessNovaOffer({
  startWith,
  action,
  fine,
  offersStrategyCall,
  bookACallUrl,
  variant = "marketing",
  className,
}: {
  /** "Start with lead generation, today." Falls back to a plain line when nothing holds her back. */
  startWith: string | null;
  action: React.ReactNode;
  fine: string | null;
  offersStrategyCall: boolean;
  /** The admin's Book-a-call link; without one the call is booked by email. */
  bookACallUrl?: string | null;
  variant?: ReadinessOfferVariant;
  className?: string;
}): React.JSX.Element {
  const styles = OFFER_STYLES[variant];
  return (
    <section className={cn(styles.panel, className)}>
      <div className="grid gap-8 @3xl:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] @3xl:items-center">
        <div className="min-w-0">
          <h2 className={styles.title}>
            NOVA AI builds your system that gets you clients
          </h2>
          <p className="text-text-secondary mt-4 max-w-prose leading-relaxed text-pretty">
            NOVA AI is Dream Coach Match's own AI, trained on what grows a
            coaching business. It builds your client acquisition system with you
            one part at a time. That includes your positioning, your offer, your
            social profiles, your content and the Mini Sprint that turns free
            sessions into clients. The 8-week Business Builder Program shows you
            what to build each week. NOVA AI builds it with you.
          </p>
          <ul className="mt-5 flex flex-col gap-2">
            {NOVA_BUILDS.map((item) => (
              <li key={item} className="text-text-primary flex gap-2">
                <Check
                  aria-hidden="true"
                  className="mt-0.5 size-4 shrink-0 text-green-600"
                  strokeWidth={2.6}
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className={cn("min-w-0", styles.buy)}>
          <p className="text-text-secondary text-pretty">
            {startWith ?? "Keep your system running, with NOVA."}
          </p>
          <div className={cn("mt-4", styles.action)}>{action}</div>
          {fine ? (
            <p className="text-text-tertiary mt-3 text-sm text-pretty">
              {fine}
            </p>
          ) : null}
          {offersStrategyCall ? (
            <p className="text-text-secondary mt-4 border-t border-gray-100 pt-4 text-sm text-pretty">
              Want to get there faster, with an advisor looking at your business
              every week?{" "}
              <a
                {...strategyCallLink(bookACallUrl)}
                className="font-medium text-purple-700 underline underline-offset-4"
              >
                Book a strategy call
              </a>
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

const STRATEGY_CALL_COVERS = [
  "Where your next $100K comes from: a higher price, a new offer, or more leverage",
  "How to grow without more hours: group programs, a team, or both",
  "What to stop doing, so the growth fits the life you want",
  "A 90-day plan you leave the call with",
] as const;

/** Past $100K the question is growth without more hours, so the call leads. */
export function ReadinessStrategyCall({
  trialAction,
  bookACallUrl,
  variant = "marketing",
  className,
}: {
  /** The second option: start the trial, or open NOVA when she already has it. */
  trialAction: React.ReactNode;
  /** The admin's Book-a-call link; without one the call is booked by email. */
  bookACallUrl?: string | null;
  variant?: ReadinessOfferVariant;
  className?: string;
}): React.JSX.Element {
  const styles = OFFER_STYLES[variant];
  // The public band centres the call, as the mock-up does.
  const centered = variant === "marketing";
  return (
    <section className={cn(styles.panel, className)}>
      <div className={centered ? "mx-auto max-w-2xl text-center" : undefined}>
        <h2 className={styles.title}>
          You're already past $100K. Want to scale your impact and your revenue?
        </h2>
        <p className="text-text-secondary mt-4 leading-relaxed text-pretty">
          At your stage the question isn't how to get clients. It's how to grow
          without working more hours. That's what the strategy call is for.
        </p>
        <ul
          className={cn(
            "mt-5 flex flex-col gap-2",
            centered && "mx-auto max-w-xl text-left",
          )}
        >
          {STRATEGY_CALL_COVERS.map((item) => (
            <li key={item} className="text-text-primary flex gap-2">
              <Check
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-green-600"
                strokeWidth={2.6}
              />
              {item}
            </li>
          ))}
        </ul>
        <div
          className={cn(
            "mt-7 flex flex-col gap-2",
            centered ? "items-center" : "items-start",
          )}
        >
          <ButtonLink
            {...strategyCallLink(bookACallUrl)}
            variant="primary"
            size="lg"
          >
            Book a strategy call
          </ButtonLink>
          <p className="text-text-tertiary text-sm">
            Free, 45 minutes, with a Dream Coach Match business advisor.
          </p>
        </div>
        <p
          className={cn(
            "text-text-secondary mt-6 text-sm text-pretty",
            centered && "border-t border-purple-200 pt-5",
          )}
        >
          Rather build it yourself first? {trialAction}
        </p>
      </div>
    </section>
  );
}
